import { calculateAge } from '../utils/miscellaneous';
import { getOperatingSystem } from '../utils/miscellaneous';
import { publicKey } from './publicKey';
import forge from 'node-forge';

/**
 * Retrieves the Nuralogix Call-In URL by making asynchronous API calls and processing data.
 *
 * @async
 * @function
 * @param {string} userAssessmentID - User assessment ID.
 * @param {object} assessmentApi - API object for assessments.
 * @param {object} questionnaireApi - API object for questionnaires.
 * @param {object} nuralogixApi - API object for Nuralogix.
 * @returns {Promise<object>} A Promise resolving to an object containing Nuralogix Call-In URL information.
 * @throws {Error} Throws an error if any API call fails.
 */
export const getNuralogixCallInUrl = async (
  userAssessmentID,
  assessmentApi,
  questionnaireApi,
  nuralogixApi
) => {
  try {
    const demographicsRes = await questionnaireApi.getQuestionnaire();
    const demographicsData = demographicsRes.data;

    const userHeight = Math.trunc( demographicsData.height );
    const userWeight = Math.trunc( demographicsData.weight );
    const userIdRes = await nuralogixApi.getNuralogixUserId();
    const userId = userIdRes.data;

    let assessmentId = userAssessmentID;
    if ( assessmentId == null ) {
      const userAssessmentRes = await assessmentApi.getLatestAssessment();
      assessmentId = userAssessmentRes.data.id;
    }

    const operatingSystem = getOperatingSystem();
    const nuralogixTokenRes = await nuralogixApi.postNuralogixToken( operatingSystem, assessmentId );
    const nuralogixTokenData = nuralogixTokenRes.data;

    const payload = {
      identifier: userId,
      age: calculateAge( demographicsData.date_of_birth ),
      //adding a 0 more because in the DB we don't have an ID of 0 but rather ID of 3 for no diabetic
      diabetes: [ '0', 'type1', 'type2', '0' ][ demographicsData.diabetes_type_id ],
      height: userHeight,
      bloodpressuremedication: demographicsData.is_on_bp_meds ? '1' : '0',
      gender: demographicsData.gender_id === 1 ? 'male' : demographicsData.gender_id === 2 ? 'female' : null,
      smoking: demographicsData.is_smoker ? '1' : '0',
      weight: userWeight,
    };

    const payloadString = JSON.stringify( payload );
    const publicKeyObject = forge.pki.publicKeyFromPem( publicKey );

    const encryptedProfile = forge.util.encode64(
      publicKeyObject.encrypt( forge.util.encodeUtf8( payloadString ), 'RSA-OAEP' )
    );

    return {
      nuralogixTokenData,
      configResponseData: process.env.REACT_APP_AIMD_CONFIG_ID,
      encryptedProfile,
    };
  } catch ( error ) {
    console.error( 'GetNuralogixCallInUrl error:', error );
    throw new Error( `Failed to get Nuralogix call-in URL: ${ error.message }` );
  }
};